import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';
import storageService from '@/services/StorageService';
import { UserRole } from '../types';

// Definindo tipos usando a tipagem gerada pelo Supabase
export type TenantSettings = Database['public']['Tables']['tenant_settings']['Row'];
export type TenantSettingsUpdate = Partial<Omit<TenantSettings, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>>;

export const useTenantSettings = () => {
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Tenta usar o contexto de autenticação, mas não falha se não estiver disponível
  let authContext;
  try {
    authContext = useAuth();
  } catch (error) {
    // Se o contexto de autenticação não estiver disponível, usa valores padrão
    authContext = {
      state: { user: null, isAuthenticated: false, isLoading: false, error: null },
      profile: null,
      user: null
    } as any;
  }

  const { user, profile } = authContext;

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!user || !profile?.tenant_id) {
        setError('Usuário não autenticado ou sem tenant associado');
        setIsLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('tenant_settings')
        .select('*')
        .eq('tenant_id', profile.tenant_id)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      if (!data) {
        // Se não existirem configurações, criar as configurações padrão
        const { data: newSettings, error: insertError } = await supabase
          .from('tenant_settings')
          .insert([{
            tenant_id: profile.tenant_id,
            chave: 'config',
            valor: '{}'
          }] as any)
          .select()
          .single();

        if (insertError) {
          throw insertError;
        }

        setSettings(newSettings);
      } else {
        setSettings(data);
      }
    } catch (err: any) {
      console.error('Erro ao buscar configurações:', err);
      setError(err.message || 'Erro desconhecido ao buscar configurações');
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (updatedSettings: TenantSettingsUpdate): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      if (!user || !profile?.tenant_id) {
        setError('Usuário não autenticado ou sem tenant associado');
        setIsLoading(false);
        return false;
      }

      // Verificar se o usuário tem permissão (admin)
      if (profile.role !== UserRole.ASSOCIADO && profile.role !== UserRole.SUPER_ADMIN) {
        throw new Error('Apenas administradores podem alterar as configurações do sistema');
      }

      const { error: updateError } = await supabase
        .from('tenant_settings')
        .update(updatedSettings)
        .eq('tenant_id', profile.tenant_id);

      if (updateError) {
        throw updateError;
      }

      // Atualizar o estado local
      setSettings((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          ...updatedSettings
        };
      });

      toast.success('Configurações atualizadas com sucesso');
      return true;
    } catch (err: any) {
      console.error('Erro ao atualizar configurações:', err);
      setError(err.message || 'Erro desconhecido ao atualizar configurações');
      toast.error('Erro ao atualizar configurações', {
        description: err.message,
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Faz o upload do logo da empresa e atualiza as configurações
   * @param file Arquivo de imagem do logo
   */
  const uploadLogo = async (file: File): Promise<boolean> => {
    if (!profile?.tenant_id) {
      toast.error('Erro ao fazer upload do logo', {
        description: 'Usuário não tem tenant associado'
      });
      return false;
    }

    try {
      setLogoUploading(true);

      // Fazer o upload do logo usando o serviço de storage
      const logoUrl = await storageService.uploadLogo(profile.tenant_id.toString(), file);

      // Atualizar a URL do logo nas configurações do tenant
      // Note: A propriedade nas configurações do banco é 'logo', não 'logo_url' se o banco definir assim.
      // Vamos verificar a tipagem gerada. Se for TS2353, 'logo_url' não existe em TenantSettingsUpdate.
      const success = await updateSettings({ logo: logoUrl } as any);

      if (success) {
        toast.success('Logo atualizado com sucesso');
      }

      return success;
    } catch (err: any) {
      console.error('Erro ao fazer upload do logo:', err);
      toast.error('Erro ao fazer upload do logo', {
        description: err.message
      });
      return false;
    } finally {
      setLogoUploading(false);
    }
  };

  // Função para recuperar uma configuração específica com valor padrão
  const getSettingValue = <T,>(key: keyof TenantSettings, defaultValue: T): T => {
    if (!settings) return defaultValue;

    const value = settings[key];
    return (value !== null && value !== undefined) ? value as unknown as T : defaultValue;
  };

  useEffect(() => {
    if (user && profile?.tenant_id) {
      fetchSettings();
    } else {
      // Se não há usuário autenticado, define loading como false
      setIsLoading(false);
    }
  }, [user, profile?.tenant_id]);

  return {
    settings,
    isLoading,
    error,
    logoUploading,
    fetchSettings,
    updateSettings,
    uploadLogo,
    getSettingValue
  };
}; 